/**
 * PdfStatementsPanel — embedded annual-report PDF uploader for the per-country
 * research wizard (ResearchCompanyDialog). The country pipeline is preset by
 * the surrounding flow and never shown as a choice here.
 *
 * Flow:
 *   1. Pick statement basis (consolidated / standalone) and drop a PDF.
 *   2. POST /finance-pdf/{india|us}-statements → 202 { job_id }.
 *   3. Poll GET /finance-pdf/statements-jobs/{job_id} every 4 s
 *      (extraction typically takes 1–4 minutes).
 *   4. On completed → onComplete(result.persistence.company_id). The company
 *      is inferred from the PDF — no manual name/year/ticker metadata.
 *
 * dry_run is deliberately never sent from the UI (backend defaults false).
 */
import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FileText, Loader2, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import Card, { CardBody, CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import { formatFiscalYear } from './utils/formatters';
import {
  completedCompanyId,
  getPdfStatementsJob,
  startPdfStatementsExtraction,
  type PdfStatementsCountry,
  type PdfStatementsJob,
  type StatementBasis,
} from '../../lib/api/financePdfApi';

const POLL_INTERVAL_MS = 4000;

/**
 * Friendlier guidance for the structured failure reasons the backend embeds
 * in the error message. Falls back to the raw error text.
 */
function failureGuidance(error: string | null | undefined): { headline: string; hint?: string } {
  const text = error ?? 'Unknown error';
  if (text.includes('company_ambiguous')) {
    return {
      headline: 'Multiple existing companies match this report',
      hint:
        'The company name in the PDF matches more than one company already in the graph. ' +
        'Rename or remove the duplicates in the Companies list, then upload again.',
    };
  }
  if (text.includes('company_not_found')) {
    return {
      headline: 'Could not read the company name from the PDF',
      hint:
        'The report header was unreadable, so no company could be identified. Check that ' +
        'the file is a text-based (not scanned) annual report with a legible cover page.',
    };
  }
  return { headline: 'PDF extraction failed' };
}

interface PdfStatementsPanelProps {
  /** Preset by the surrounding country flow — no picker is rendered. */
  country: PdfStatementsCountry;
  /** Fires when extraction completed and persisted a companyId. */
  onComplete: (companyId: string) => void;
  /** Back out to the source-selection step. */
  onCancel: () => void;
  /**
   * Notifies the host while a job is in flight so it can lock its own escape
   * hatches (Escape / backdrop / country "Change") — closing mid-job loses the
   * only UI tracking the extraction.
   */
  onRunningChange?: (running: boolean) => void;
}

export default function PdfStatementsPanel({
  country,
  onComplete,
  onCancel,
  onRunningChange,
}: PdfStatementsPanelProps) {
  const [basis, setBasis] = useState<StatementBasis>('consolidated');
  const [file, setFile] = useState<File | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<PdfStatementsJob | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  // Guards the completion callback against firing twice if a poll response
  // lands while the redirect timeout is already pending.
  const completedRef = useRef(false);
  // Stable ref so the polling effect doesn't re-register when the parent
  // re-renders with a fresh onComplete closure.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const isRunning =
    !!jobId && (!job || job.status === 'pending' || job.status === 'processing');

  const onRunningChangeRef = useRef(onRunningChange);
  useEffect(() => { onRunningChangeRef.current = onRunningChange; }, [onRunningChange]);
  useEffect(() => {
    onRunningChangeRef.current?.(isRunning);
    // On unmount, release any lock the host holds on our behalf.
    return () => { onRunningChangeRef.current?.(false); };
  }, [isRunning]);

  // Poll the job until it settles. Transient poll errors are swallowed — the
  // extraction keeps running server-side, so the next tick usually succeeds.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      let status: PdfStatementsJob | null = null;
      try {
        status = await getPdfStatementsJob(jobId);
      } catch (err) {
        console.warn('[finance-pdf] poll failed, retrying…', err);
      }
      if (cancelled) return;

      if (status) {
        setJob(status);
        if (status.status === 'completed') {
          if (!completedRef.current) {
            completedRef.current = true;
            const companyId = completedCompanyId(status);
            if (companyId) {
              toast.success('Financial statements extracted — opening company view…');
              timer = window.setTimeout(() => {
                if (!cancelled) onCompleteRef.current(companyId);
              }, 1200);
            } else {
              toast.warning('Extraction finished but returned no company id.');
            }
          }
          return;
        }
        if (status.status === 'failed') {
          toast.error(failureGuidance(status.error).headline);
          return;
        }
      }
      timer = window.setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [jobId]);

  // Elapsed-time ticker while the job runs — a 1–4 minute wait with a frozen
  // spinner reads as "hung"; a counting clock reads as "working".
  useEffect(() => {
    if (!isRunning) return;
    const interval = window.setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => window.clearInterval(interval);
  }, [isRunning]);

  const acceptPdf = (candidate: File | undefined | null) => {
    if (!candidate) return;
    if (candidate.type !== 'application/pdf' && !candidate.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    setFile(candidate);
  };

  const handleStart = async () => {
    if (!file || isStarting || isRunning) return;
    setIsStarting(true);
    try {
      const started = await startPdfStatementsExtraction(country, file, { basis });
      completedRef.current = false;
      setElapsedSec(0);
      setJob(null);
      setJobId(started.job_id);
      toast.info(started.message || 'Extraction started — this takes a few minutes.');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : err instanceof Error
            ? err.message
            : 'Failed to start extraction';
      toast.error(msg);
    } finally {
      setIsStarting(false);
    }
  };

  const handleReset = () => {
    setJobId(null);
    setJob(null);
    setFile(null);
    setElapsedSec(0);
    completedRef.current = false;
  };

  const elapsedLabel = `${Math.floor(elapsedSec / 60)}:${String(elapsedSec % 60).padStart(2, '0')}`;
  const jobCompanyId = job ? completedCompanyId(job) : null;
  const yearsExtracted = job?.result?.years_extracted ?? [];
  const failure = job?.status === 'failed' ? failureGuidance(job.error) : null;
  const fileInputId = `finance-pdf-upload-${country.toLowerCase()}`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-neutral-900 mb-1">Upload Annual Report PDF</h2>
        <p className="text-sm text-neutral-600">
          {country === 'INDIA'
            ? 'India annual report (values in ₹ crore). '
            : 'US annual report / 10-K filing (values in USD millions). '}
          The company is identified from the PDF and appears in the same views as web-scraped
          companies — no manual details needed.
        </p>
      </div>

      {/* Step 1 — configure & upload */}
      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold text-neutral-900">1 · Statement basis & file</h3>
        </CardHeader>
        <CardBody className="space-y-6">
          {/* Statement basis */}
          <div>
            <label
              htmlFor={`statement-basis-${country.toLowerCase()}`}
              className="block text-sm font-medium text-neutral-700 mb-1.5"
            >
              Statement basis
            </label>
            <select
              id={`statement-basis-${country.toLowerCase()}`}
              value={basis}
              disabled={isRunning}
              onChange={(e) => setBasis(e.target.value as StatementBasis)}
              className="w-full p-2.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-800 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
            >
              <option value="consolidated">Consolidated (recommended)</option>
              <option value="standalone">Standalone</option>
            </select>
            <p className="text-xs text-neutral-500 mt-1.5">
              Which set of audited statements to extract. Consolidated includes subsidiaries;
              Standalone is the parent company only.
              {country === 'US' && ' US filings are consolidated; the choice mainly labels the company in the research list.'}
            </p>
          </div>

          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              file
                ? 'border-green-500 bg-green-50'
                : 'border-neutral-300 hover:border-primary-500 hover:bg-primary-50'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isRunning) acceptPdf(e.dataTransfer.files?.[0]);
            }}
          >
            {file ? (
              <div className="space-y-3">
                <FileText className="h-12 w-12 text-green-600 mx-auto" />
                <div>
                  <div className="text-green-900 font-medium">{file.name}</div>
                  <div className="text-sm text-green-700">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                {!isRunning && (
                  <Button variant="outline" size="sm" onClick={() => setFile(null)}>
                    Remove File
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="h-12 w-12 text-neutral-400 mx-auto" />
                <div>
                  <div className="text-neutral-900 font-medium mb-1">Drag & drop the PDF here</div>
                  <div className="text-sm text-neutral-600 mb-3">or</div>
                  <label htmlFor={fileInputId}>
                    <input
                      id={fileInputId}
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        acceptPdf(e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    <Button variant="primary" size="sm" as="span">
                      Browse Files
                    </Button>
                  </label>
                </div>
                <div className="text-xs text-neutral-500">
                  {country === 'INDIA' ? 'India annual report' : 'US 10-K filing'} · PDF format
                </div>
              </div>
            )}
          </div>

          <Button
            variant="primary"
            className="w-full"
            onClick={handleStart}
            disabled={!file || isStarting || isRunning}
          >
            {isStarting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : (
              'Start Extraction'
            )}
          </Button>
        </CardBody>
      </Card>

      {/* Step 2 — job status */}
      {jobId && (
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-neutral-900">2 · Extraction status</h3>
          </CardHeader>
          <CardBody>
            {isRunning && (
              <div className="flex items-start gap-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <Loader2 className="h-6 w-6 text-blue-600 animate-spin shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-blue-900">
                    Extracting financial statements from the PDF — this takes a few minutes.
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {job?.status === 'processing' ? 'Processing' : 'Queued'}
                    {job?.filename ? ` · ${job.filename}` : file ? ` · ${file.name}` : ''}
                    {' · elapsed '}{elapsedLabel}
                  </p>
                  <p className="text-[11px] text-blue-600/80 mt-1.5">
                    Job {jobId} — you can keep this open; it checks progress every few seconds.
                  </p>
                </div>
              </div>
            )}

            {job?.status === 'completed' && (
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-100 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      {jobCompanyId
                        ? 'Extraction complete — opening the company view…'
                        : 'Extraction complete.'}
                    </p>
                    {yearsExtracted.length > 0 && (
                      <p className="text-xs text-green-700 mt-1">
                        Fiscal years extracted:{' '}
                        {yearsExtracted.map((y) => formatFiscalYear(y)).join(', ')}
                      </p>
                    )}
                    {jobCompanyId ? (
                      <p className="text-xs text-green-700 mt-1">
                        Company ID: <span className="font-mono">{jobCompanyId}</span>
                        {job.result?.persistence?.wrote_to_neo4j === false &&
                          ' · warning: storage not confirmed'}
                      </p>
                    ) : (
                      <p className="text-xs text-green-700 mt-1">
                        No company id was returned — check the Companies list for the imported
                        company.
                      </p>
                    )}
                  </div>
                </div>

                {jobCompanyId ? (
                  <Button variant="primary" onClick={() => onComplete(jobCompanyId)}>
                    Open Company
                  </Button>
                ) : (
                  <Button variant="outline" onClick={handleReset}>
                    Start Another Import
                  </Button>
                )}
              </div>
            )}

            {failure && (
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-red-50 border border-red-100 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900">{failure.headline}</p>
                    {failure.hint && (
                      <p className="text-xs text-red-700 mt-1">{failure.hint}</p>
                    )}
                    <p className="text-[11px] text-red-600/80 mt-1.5 break-words">
                      {job?.error ?? 'Unknown error'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" onClick={handleReset}>
                  Try Again
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <div>
        <Button variant="outline" onClick={onCancel} disabled={isRunning}>Back</Button>
        {isRunning && (
          <p className="text-xs text-neutral-500 mt-2">
            Please keep this open until extraction finishes — leaving now would lose progress tracking.
          </p>
        )}
      </div>
    </div>
  );
}
